import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { authMe, authLogout, adminListOrders, adminCreateBootcamp, adminDeleteBootcamp, adminCreateModule, adminDeleteModule, listBootcamps, listModules } from "../lib/api";
import { toast } from "sonner";
import { SignOut, Plus, Trash, GoogleLogo } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startLogin() {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function LoginGate() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-obsidian-700 border border-white/5 p-10 text-center">
        <div className="font-bebas text-4xl uppercase">Admin</div>
        <p className="text-zinc-400 mt-3">Sign in with Google to manage sessions, modules and orders.</p>
        <Button
          onClick={startLogin}
          data-testid="admin-login-btn"
          className="w-full mt-6 bg-white text-black hover:bg-gold-400 rounded-none font-bebas text-lg tracking-wider"
        >
          <GoogleLogo size={20} weight="bold" className="mr-2" /> Sign in with Google
        </Button>
      </div>
    </div>
  );
}

const emptyBC = { title: "", tier: "fundamentals", description: "", starts_at: "", duration_minutes: 90, capacity: 20, price_usd: 499, zoom_link: "" };
const emptyMod = { title: "", subtitle: "POKER 101 — Module ", description: "", price_usd: 39, module_number: 1, duration_minutes: 45, topics: "" };

export default function Admin() {
  const [state, setState] = useState({ user: null, checked: false });
  const [orders, setOrders] = useState([]);
  const [bootcamps, setBootcamps] = useState([]);
  const [modules, setModules] = useState([]);
  const [bcForm, setBcForm] = useState(emptyBC);
  const [modForm, setModForm] = useState(emptyMod);
  const nav = useNavigate();

  const refresh = async () => {
    try {
      const [o, b, m] = await Promise.all([adminListOrders(), listBootcamps(), listModules()]);
      setOrders(o);
      setBootcamps(b);
      setModules(m);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    (async () => {
      // If URL still has session_id hash, callback route handles it.
      if (window.location.hash?.includes("session_id=")) return;
      try {
        const me = await authMe();
        setState({ user: me, checked: true });
        refresh();
      } catch {
        setState({ user: null, checked: true });
      }
    })();
  }, []);

  if (!state.checked) {
    return <div className="text-center py-24 font-mono text-zinc-500">Checking session…</div>;
  }
  if (!state.user) return <LoginGate />;

  const doLogout = async () => {
    await authLogout();
    setState({ user: null, checked: true });
  };

  const createBC = async (e) => {
    e.preventDefault();
    try {
      await adminCreateBootcamp({ ...bcForm, capacity: Number(bcForm.capacity), price_usd: Number(bcForm.price_usd), duration_minutes: Number(bcForm.duration_minutes) });
      toast.success("Boot Camp created");
      setBcForm(emptyBC);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const createMod = async (e) => {
    e.preventDefault();
    try {
      await adminCreateModule({
        ...modForm,
        price_usd: Number(modForm.price_usd),
        module_number: Number(modForm.module_number),
        duration_minutes: Number(modForm.duration_minutes),
        topics: modForm.topics.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Module created");
      setModForm(emptyMod);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-gold-400">Admin Console</div>
          <h1 className="font-bebas text-5xl uppercase mt-1">Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          {state.user?.picture && <img src={state.user.picture} alt="" className="w-10 h-10 rounded-full border border-white/10" />}
          <div className="text-right">
            <div className="text-sm">{state.user.name}</div>
            <div className="text-xs font-mono text-zinc-500">{state.user.email}</div>
          </div>
          <Button onClick={doLogout} variant="outline" data-testid="admin-logout-btn" className="border-white/10 bg-transparent text-white hover:bg-white/5 rounded-none font-bebas tracking-wider">
            <SignOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </div>

      {/* Orders */}
      <section className="mb-16">
        <h2 className="font-bebas text-3xl uppercase mb-4">Orders</h2>
        <div className="bg-obsidian-700 border border-white/5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 font-mono text-xs uppercase tracking-widest border-b border-white/5">
                <th className="p-3">When</th>
                <th className="p-3">Kind</th>
                <th className="p-3">Item</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Email</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="admin-orders-table">
              {orders.length === 0 && (
                <tr><td colSpan="6" className="p-6 text-center text-zinc-500 font-mono">No orders yet.</td></tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-white/5">
                  <td className="p-3 font-mono text-zinc-400">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="p-3 uppercase font-mono text-xs text-gold-400">{o.kind}</td>
                  <td className="p-3">{o.item_title}</td>
                  <td className="p-3">{o.customer_name}</td>
                  <td className="p-3 font-mono text-zinc-400">{o.customer_email}</td>
                  <td className="p-3"><span className="border border-felt text-felt px-2 py-0.5 text-xs font-mono uppercase">{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Boot Camps */}
      <section className="mb-16 grid lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-bebas text-3xl uppercase mb-4">Boot Camp Sessions</h2>
          <div className="bg-obsidian-700 border border-white/5">
            {bootcamps.length === 0 && <div className="p-6 text-center text-zinc-500 font-mono">No sessions.</div>}
            {bootcamps.map((b) => (
              <div key={b.id} className="border-b border-white/5 p-4 flex items-center justify-between">
                <div>
                  <div className="font-bebas text-xl uppercase">{b.title}</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1">
                    {b.tier} · {new Date(b.starts_at).toLocaleDateString()} · {b.seats_taken}/{b.capacity} · ${b.price_usd}
                  </div>
                </div>
                <Button
                  onClick={async () => { await adminDeleteBootcamp(b.id); refresh(); toast.success("Deleted"); }}
                  variant="outline"
                  data-testid={`admin-delete-bc-${b.id}`}
                  className="border-scarcity/50 text-scarcity bg-transparent hover:bg-scarcity/10 rounded-none"
                >
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-bebas text-2xl uppercase mb-4 text-gold-400">Create new session</h3>
          <form onSubmit={createBC} className="space-y-3 bg-obsidian-700 border border-white/5 p-5">
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Title</Label>
              <Input value={bcForm.title} onChange={(e) => setBcForm({ ...bcForm, title: e.target.value })} required className="bg-obsidian-900 border-white/10 text-white rounded-none mt-1" data-testid="admin-bc-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Tier</Label>
                <select value={bcForm.tier} onChange={(e) => setBcForm({ ...bcForm, tier: e.target.value })} className="w-full bg-obsidian-900 border border-white/10 text-white px-3 py-2 mt-1 font-mono text-sm">
                  <option value="fundamentals">fundamentals</option>
                  <option value="deep-work">deep-work</option>
                  <option value="q-and-a">q-and-a</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Starts (ISO)</Label>
                <Input type="datetime-local" value={bcForm.starts_at} onChange={(e) => setBcForm({ ...bcForm, starts_at: e.target.value })} required className="bg-obsidian-900 border-white/10 text-white rounded-none mt-1" />
              </div>
            </div>
            <Textarea value={bcForm.description} onChange={(e) => setBcForm({ ...bcForm, description: e.target.value })} placeholder="Description" required className="bg-obsidian-900 border-white/10 text-white rounded-none" />
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Capacity</Label>
                <Input type="number" value={bcForm.capacity} onChange={(e) => setBcForm({ ...bcForm, capacity: e.target.value })} className="bg-obsidian-900 border-white/10 text-white rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Duration (min)</Label>
                <Input type="number" value={bcForm.duration_minutes} onChange={(e) => setBcForm({ ...bcForm, duration_minutes: e.target.value })} className="bg-obsidian-900 border-white/10 text-white rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Price $</Label>
                <Input type="number" step="0.01" value={bcForm.price_usd} onChange={(e) => setBcForm({ ...bcForm, price_usd: e.target.value })} className="bg-obsidian-900 border-white/10 text-white rounded-none mt-1" />
              </div>
            </div>
            <Input value={bcForm.zoom_link} onChange={(e) => setBcForm({ ...bcForm, zoom_link: e.target.value })} placeholder="Zoom link" className="bg-obsidian-900 border-white/10 text-white rounded-none" />
            <Button type="submit" data-testid="admin-bc-submit" className="w-full bg-gradient-to-r from-gold-400 to-gold-500 text-black hover:brightness-110 rounded-none font-bebas text-lg tracking-wider">
              <Plus size={16} className="mr-2" /> Create Session
            </Button>
          </form>
        </div>
      </section>

      {/* Modules */}
      <section className="grid lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-bebas text-3xl uppercase mb-4">POKER 101 Modules</h2>
          <div className="bg-obsidian-700 border border-white/5">
            {modules.map((m) => (
              <div key={m.id} className="border-b border-white/5 p-4 flex items-center justify-between">
                <div>
                  <div className="font-bebas text-xl uppercase">M{m.module_number} · {m.title}</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1">${m.price_usd} · {m.duration_minutes} min</div>
                </div>
                <Button
                  onClick={async () => { await adminDeleteModule(m.id); refresh(); toast.success("Deleted"); }}
                  variant="outline"
                  data-testid={`admin-delete-mod-${m.id}`}
                  className="border-scarcity/50 text-scarcity bg-transparent hover:bg-scarcity/10 rounded-none"
                >
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-bebas text-2xl uppercase mb-4 text-gold-400">Create new module</h3>
          <form onSubmit={createMod} className="space-y-3 bg-obsidian-700 border border-white/5 p-5">
            <Input value={modForm.title} onChange={(e) => setModForm({ ...modForm, title: e.target.value })} required placeholder="Title" className="bg-obsidian-900 border-white/10 text-white rounded-none" data-testid="admin-mod-title" />
            <Input value={modForm.subtitle} onChange={(e) => setModForm({ ...modForm, subtitle: e.target.value })} placeholder="Subtitle" className="bg-obsidian-900 border-white/10 text-white rounded-none" />
            <Textarea value={modForm.description} onChange={(e) => setModForm({ ...modForm, description: e.target.value })} placeholder="Description" required className="bg-obsidian-900 border-white/10 text-white rounded-none" />
            <div className="grid grid-cols-3 gap-3">
              <Input type="number" value={modForm.module_number} onChange={(e) => setModForm({ ...modForm, module_number: e.target.value })} placeholder="Module #" className="bg-obsidian-900 border-white/10 text-white rounded-none" />
              <Input type="number" step="0.01" value={modForm.price_usd} onChange={(e) => setModForm({ ...modForm, price_usd: e.target.value })} placeholder="Price" className="bg-obsidian-900 border-white/10 text-white rounded-none" />
              <Input type="number" value={modForm.duration_minutes} onChange={(e) => setModForm({ ...modForm, duration_minutes: e.target.value })} placeholder="Duration (min)" className="bg-obsidian-900 border-white/10 text-white rounded-none" />
            </div>
            <Input value={modForm.topics} onChange={(e) => setModForm({ ...modForm, topics: e.target.value })} placeholder="Topics (comma-separated)" className="bg-obsidian-900 border-white/10 text-white rounded-none" />
            <Button type="submit" data-testid="admin-mod-submit" className="w-full bg-gradient-to-r from-gold-400 to-gold-500 text-black hover:brightness-110 rounded-none font-bebas text-lg tracking-wider">
              <Plus size={16} className="mr-2" /> Create Module
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
